import axios, { AxiosInstance, AxiosError } from 'axios';
import { ApiError, HealthCheckResponse } from '../types/api';
import { userManager } from '../auth/oidcConfig';
import { config } from '../config';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor — attach Dex id_token from in-memory OIDC session
    this.client.interceptors.request.use(
      async (config) => {
        const user = await userManager.getUser();
        if (user?.id_token) {
          config.headers.Authorization = `Bearer ${user.id_token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // The session is gone (expired token, failed silent renew). Drop it so
        // the app falls back to the login gate instead of retrying blind — the
        // `userUnloaded` event is what App listens on.
        if (error.response?.status === 401) {
          await userManager.removeUser().catch(() => undefined);
        }
        // Carry the backend's own error envelope through. Status alone cannot
        // separate a revision conflict from a geofence violation — both 409 —
        // and the details name which waypoints failed (issue #451).
        const envelope = (
          error.response?.data as
            | { error?: { code?: string; message?: string; details?: Record<string, string> } }
            | undefined
        )?.error;

        const apiError: ApiError = {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          serverCode: envelope?.code,
          serverMessage: envelope?.message,
          details: envelope?.details,
        };
        return Promise.reject(apiError);
      }
    );
  }

  // The api-backend writes payloads unwrapped — `writeJSON` encodes the
  // response struct straight onto the wire, with no `{ data: ... }` envelope
  // (issue #398). These helpers therefore resolve to the response body itself;
  // `response.data` below is axios's own parsed-body field, not an envelope.
  async get<T>(url: string): Promise<T> {
    const response = await this.client.get<T>(url);
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  /**
   * Liveness of the api-backend.
   *
   * The path is `/healthz`, not `/api/v1/health` — which is what this called
   * until #516, and which has never existed. Health and readiness are on the
   * top-level mux, deliberately outside the authenticated `/api/v1` tree
   * (`cmd/api-backend/main.go`), so a probe needs no credential.
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    return this.get<HealthCheckResponse>('/healthz');
  }
}

export const apiClient = new ApiClient();
